import React from 'react';

/**
 * ChildIdentityHeader
 * -------------------
 * The child's identity, the same way on every triage screen (TDI-392, from
 * TDI-278): full name, date of birth, NHS number where given, and the
 * platform reference. Mounted directly under the top navigation on the
 * proforma (both views), every decision step, the Documents page and the
 * letter review, and sticky beneath the nav, so a clinician always knows
 * whose referral they are working on and a decision is never recorded on the
 * wrong child. It replaces the per-screen name headings.
 *
 * Reads the record alone: subject.* first, then the flat legacy fields the
 * oldest referrals carry. A missing value says "Not recorded" rather than
 * disappearing, so the row never shifts and a gap is a fact the clinician
 * sees.
 */

export const NOT_RECORDED = 'Not recorded';

function formatDob(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    }
  }
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * The four identifiers from a patient record. Pure, so every screen shows
 * the same thing for the same record.
 *
 * @param {object} patient - the patient record document
 * @param {string} patientRecordId - the document id (the platform reference)
 * @returns {{ name: string, dob: string, nhsNumber: string, reference: string }}
 */
export function childIdentity(patient, patientRecordId) {
  const d = patient || {};
  const subject = d.subject || {};
  const first = String(subject.firstName || d.subject_first_name || d.firstName || '').trim();
  const last = String(subject.lastName || d.subject_last_name || d.lastName || '').trim();
  const name = [first, last].filter(Boolean).join(' ') || String(d.fullName || '').trim();
  const dob = formatDob(subject.dob || d.subject_dob || d.dateOfBirth || d.dob || null);
  const nhsNumber = String(subject.nhsNumber || '').trim();
  return {
    name: name || 'Unknown patient',
    dob: dob || NOT_RECORDED,
    nhsNumber: nhsNumber || NOT_RECORDED,
    reference: String(patientRecordId || d.id || '').trim() || NOT_RECORDED,
  };
}

export default function ChildIdentityHeader({ patient, patientRecordId, loading = false }) {
  const identity = childIdentity(patient, patientRecordId);
  const items = [
    { label: 'Date of birth', value: identity.dob },
    { label: 'NHS number', value: identity.nhsNumber },
    { label: 'Reference', value: identity.reference },
  ];
  return (
    <div
      role="region"
      aria-label="Child"
      aria-busy={loading || undefined}
      className="sticky top-16 z-40 border-b border-gray-200 bg-white px-6 py-2 shadow-sm sm:px-8 lg:px-12"
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-baseline gap-x-6 gap-y-1 text-left">
        <p className="text-base font-semibold text-gray-900">
          {loading && !patient ? 'Loading the referral…' : identity.name}
        </p>
        <dl className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm text-gray-700">
          {items.map((item) => (
            <div key={item.label} className="flex items-baseline gap-1.5">
              <dt className="text-gray-500">{item.label}</dt>
              <dd className={item.value === NOT_RECORDED ? 'text-gray-500' : 'font-medium text-gray-900'}>
                {loading && !patient ? '…' : item.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
